/**
 * Which time zone the dashboard shows times in (Galley #21943, the
 * owner's ask: "always obvious what time zone triage thinks you are in,
 * and you can change at will easily").
 *
 * Two choices, kept per browser in `localStorage`:
 * - `"local"`: the viewer's own zone. The default, and what every page
 *   showed before this switch existed.
 * - `"utc"`: UTC.
 *
 * Day and week buckets are not affected: the server cuts them at 00:00
 * UTC, so they are UTC days in either mode and say so where they show.
 *
 * The formatters in `lib/utils.ts` read `getTimeZonePref()` at call time.
 * `TimeZoneRerender` (App.tsx) renders the routed page again when the
 * choice changes, keeping its state, so every time on it is formatted
 * again without each component having to subscribe.
 *
 * A choice made in another tab is not followed until this tab loads again
 * (ticketyboo, #22187): this tab's times would change under a reader who
 * did nothing here. Each tab's top
 * bar names the zone that tab shows, so two tabs can differ honestly.
 */
import { useSyncExternalStore } from "react";

export type TimeZonePref = "local" | "utc";

/** The day-bucket note the switch shows (and the docs repeat). */
export const UTC_DAYS_NOTE =
  "Daily bars and day counts are always UTC days (00:00–24:00 UTC), in either mode.";

/** The `localStorage` key. Exported for tests. */
export const TIME_ZONE_STORAGE_KEY = "triage.timeZone";

function readStored(): TimeZonePref {
  try {
    return globalThis.localStorage?.getItem(TIME_ZONE_STORAGE_KEY) === "utc"
      ? "utc"
      : "local";
  } catch {
    // Storage blocked (privacy mode, sandboxed frame): today's zone.
    return "local";
  }
}

let current: TimeZonePref = readStored();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** The choice in effect. */
export function getTimeZonePref(): TimeZonePref {
  return current;
}

/** Change the choice, keep it for this browser, and tell subscribers. */
export function setTimeZonePref(next: TimeZonePref): void {
  if (next === current) return;
  current = next;
  try {
    globalThis.localStorage?.setItem(TIME_ZONE_STORAGE_KEY, next);
  } catch {
    // Not kept past this page load; the switch still works now.
  }
  notify();
}

/** Re-read storage, as a page load does. Exported for tests. */
export function reloadTimeZonePref(): void {
  const stored = readStored();
  if (stored !== current) {
    current = stored;
    notify();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The choice, re-rendering when this tab's switch changes it. */
export function useTimeZonePref(): TimeZonePref {
  return useSyncExternalStore(subscribe, getTimeZonePref, () => "local");
}

/**
 * The `timeZone` option for `Intl` / `toLocale*String` calls:
 * `undefined` (the viewer's zone) or `"UTC"`.
 */
export function displayTimeZone(
  pref: TimeZonePref = current,
): string | undefined {
  return pref === "utc" ? "UTC" : undefined;
}

/**
 * The `timeZone` a chart's hour axis and tooltips use: `undefined` (the
 * viewer's zone) or `"UTC"`. Day and week buckets ignore it: they are
 * UTC days in either mode (`StackedBarChart`'s `isCalendar`).
 */
export function chartTimeZone(
  pref: TimeZonePref = current,
): string | undefined {
  return displayTimeZone(pref);
}

/**
 * A date and time in the chosen zone, named, e.g.
 * `10/23/2026, 6:08:00 PM GMT+2` or `… 4:08:00 PM UTC`. `options` are
 * `Intl.DateTimeFormat` options (the zone ones are set here); with
 * `label: false` the zone's name is left off, for a place that already
 * says it once.
 */
export function formatDateTime(
  iso: string | null | undefined,
  options: Intl.DateTimeFormatOptions & { label?: boolean } = {},
): string {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return iso;
  const { label = true, ...intl } = options;
  return new Date(ts).toLocaleString(undefined, {
    ...intl,
    timeZone: displayTimeZone(),
    ...(label ? { timeZoneName: "short" } : {}),
  });
}

/** The viewer's own IANA zone name, e.g. "Europe/Amsterdam" ("" if unknown). */
export function localZoneName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
  } catch {
    return "";
  }
}

/**
 * The short name of the zone times are shown in, at `date` (summer and
 * winter differ): "UTC", "CEST", "EDT", or the locale's "GMT+2" form.
 */
export function zoneAbbrev(
  date: Date = new Date(),
  pref: TimeZonePref = current,
): string {
  if (pref === "utc") return "UTC";
  try {
    const part = new Intl.DateTimeFormat(undefined, {
      timeZoneName: "short",
    })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName");
    return part?.value ?? "";
  } catch {
    return "";
  }
}

/** The viewer's offset from UTC at `date`, as "UTC+2", "UTC-4", "UTC+5:30". */
export function localUtcOffsetLabel(date: Date = new Date()): string {
  const minutes = -date.getTimezoneOffset();
  if (minutes === 0) return "UTC+0";
  const sign = minutes > 0 ? "+" : "-";
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
}
